import api from './api';
import { Categoria, Proveedor, Producto, ProductoFormData, ApiResponse } from '../types';

// Servicios para Categorías
export const categoriaService = {
  getAll: (params?: { activo?: boolean }) => 
    api.get<ApiResponse<Categoria>>('/productos/categorias/', { params }),
  
  getById: (id: number) => 
    api.get<Categoria>(`/productos/categorias/${id}/`),
  
  create: (data: Omit<Categoria, 'id' | 'fecha_creacion' | 'fecha_actualizacion' | 'productos_count'>) => 
    api.post<Categoria>('/productos/categorias/', data),
  
  update: (id: number, data: Partial<Categoria>) => 
    api.put<Categoria>(`/productos/categorias/${id}/`, data),
  
  delete: (id: number) => 
    api.delete(`/productos/categorias/${id}/`),
  
  toggleStatus: (id: number) => 
    api.post(`/productos/categorias/${id}/toggle_status/`),
};

// Servicios para Proveedores
export const proveedorService = {
  getAll: (params?: { activo?: boolean }) => 
    api.get<ApiResponse<Proveedor>>('/productos/proveedores/', { params }),
  
  getById: (id: number) => 
    api.get<Proveedor>(`/productos/proveedores/${id}/`),
  
  create: (data: Omit<Proveedor, 'id' | 'fecha_creacion' | 'productos_count'>) => 
    api.post<Proveedor>('/productos/proveedores/', data),
  
  update: (id: number, data: Partial<Proveedor>) => 
    api.put<Proveedor>(`/productos/proveedores/${id}/`, data),
  
  delete: (id: number) => 
    api.delete(`/productos/proveedores/${id}/`),
  
  toggleStatus: (id: number) => 
    api.post(`/productos/proveedores/${id}/toggle_status/`),
};

// Lista de productos simulados para respaldo
const PRODUCTOS_MOCK: Producto[] = [
  {
    id: 1,
    codigo: 'ARR001',
    nombre: 'Arroz Grado 1',
    descripcion: 'Arroz grado 1, saco de 25kg',
    categoria: 1,
    categoria_nombre: 'Granos',
    proveedor: 1,
    proveedor_nombre: 'Distribuidora Central',
    precio_compra: 12000,
    precio_venta: 15000,
    unidad_medida: 'KG',
    stock_minimo: 10,
    stock_actual: 50,
    activo: true,
    fecha_creacion: '2024-01-01T00:00:00Z',
    fecha_actualizacion: '2024-01-01T00:00:00Z',
    necesita_reposicion: false,
    margen_ganancia: 25,
    imagen: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400'
  },
  {
    id: 2,
    codigo: 'ACE001',
    nombre: 'Aceite Vegetal 1L',
    descripcion: 'Aceite vegetal premium 1 litro',
    categoria: 2,
    categoria_nombre: 'Aceites',
    proveedor: 2,
    proveedor_nombre: 'Alimentos del Sur',
    precio_compra: 2000,
    precio_venta: 2500,
    unidad_medida: 'L',
    stock_minimo: 5,
    stock_actual: 3,
    activo: true,
    fecha_creacion: '2024-01-01T00:00:00Z',
    fecha_actualizacion: '2024-01-01T00:00:00Z',
    necesita_reposicion: true,
    margen_ganancia: 25,
    imagen: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400'
  },
  {
    id: 3,
    codigo: 'AZU001',
    nombre: 'Azúcar Granulada 2kg',
    descripcion: 'Azúcar granulada de alta calidad 2kg',
    categoria: 3,
    categoria_nombre: 'Endulzantes',
    proveedor: 3,
    proveedor_nombre: 'Azucarera Nacional',
    precio_compra: 2500,
    precio_venta: 3200,
    unidad_medida: 'KG',
    stock_minimo: 8,
    stock_actual: 0,
    activo: true,
    fecha_creacion: '2024-01-01T00:00:00Z',
    fecha_actualizacion: '2024-01-01T00:00:00Z',
    necesita_reposicion: true,
    margen_ganancia: 28,
    imagen: 'https://images.unsplash.com/photo-1559181567-c3190ca9959b?w=400'
  },
  {
    id: 4,
    codigo: 'FRI001',
    nombre: 'Frijoles Negros 1kg',
    descripcion: 'Frijoles negros premium 1kg',
    categoria: 1,
    categoria_nombre: 'Granos',
    proveedor: 4,
    proveedor_nombre: 'Granos del Campo',
    precio_compra: 2200,
    precio_venta: 2800,
    unidad_medida: 'KG',
    stock_minimo: 10,
    stock_actual: 25,
    activo: true,
    fecha_creacion: '2024-01-01T00:00:00Z',
    fecha_actualizacion: '2024-01-01T00:00:00Z',
    necesita_reposicion: false,
    margen_ganancia: 27,
    imagen: 'https://images.unsplash.com/photo-1591984672646-2e7d1b6cf8b5?w=400'
  },
  {
    id: 5,
    codigo: 'HAR001',
    nombre: 'Harina de Trigo 1kg',
    descripcion: 'Harina de trigo para todo uso 1kg',
    categoria: 4,
    categoria_nombre: 'Harinas',
    proveedor: 5,
    proveedor_nombre: 'Molinos del Norte',
    precio_compra: 1400,
    precio_venta: 1800,
    unidad_medida: 'KG',
    stock_minimo: 15,
    stock_actual: 40,
    activo: true,
    fecha_creacion: '2024-01-01T00:00:00Z',
    fecha_actualizacion: '2024-01-01T00:00:00Z',
    necesita_reposicion: false,
    margen_ganancia: 29,
    imagen: 'https://images.unsplash.com/photo-1574323347407-f0e1717d4a30?w=400'
  }
];

// Servicios para Productos
export type { Producto } from '../types';

export const productoService = {
  // Métodos originales
  getAll: (params?: { 
    activo?: boolean;
    categoria?: number;
    proveedor?: number;
    search?: string;
    stock_bajo?: boolean;
  }) => 
    api.get<ApiResponse<Producto>>('/productos/productos/', { params }),
  
  getById: (id: number) => 
    api.get<Producto>(`/productos/productos/${id}/`),
  
  create: (data: ProductoFormData) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'imagen' && value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, value.toString());
        }
      }
    });
    
    return api.post<Producto>('/productos/productos/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  update: (id: number, data: Partial<ProductoFormData>) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'imagen' && value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, value.toString());
        }
      }
    });
    
    return api.put<Producto>(`/productos/productos/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  delete: (id: number) => 
    api.delete(`/productos/productos/${id}/`),
  
  toggleStatus: (id: number) => 
    api.post(`/productos/productos/${id}/toggle_status/`),
  
  getStockBajo: () => 
    api.get<Producto[]>('/productos/productos/stock_bajo/'),

  // Métodos con alias más simples y respaldo
  getProductos: async (params?: { 
    activo?: boolean;
    categoria?: number;
    proveedor?: number;
    search?: string;
    stock_bajo?: boolean;
  }): Promise<Producto[]> => {
    try {
      console.log('🔄 Intentando cargar productos desde API local...');
      const response = await api.get<ApiResponse<Producto>>('/productos/productos/', { params });
      
      if (response.data.results && Array.isArray(response.data.results)) {
        console.log('✅ Productos cargados desde API local:', response.data.results.length);
        return response.data.results;
      } else if (Array.isArray(response.data)) {
        console.log('✅ Productos cargados desde API local (formato directo):', response.data.length);
        return response.data;
      } else {
        throw new Error('Formato de respuesta inesperado');
      }
    } catch (error) {
      console.warn('⚠️ Error al cargar desde API local, usando productos de respaldo:', error);
      console.log('📦 Productos de respaldo disponibles:', PRODUCTOS_MOCK.length);
      return PRODUCTOS_MOCK;
    }
  },

  getProducto: async (id: number): Promise<Producto | null> => {
    try {
      const response = await api.get<Producto>(`/productos/productos/${id}/`);
      return response.data;
    } catch (error) {
      console.warn('⚠️ Error al cargar producto desde API, buscando en respaldo:', error);
      return PRODUCTOS_MOCK.find(p => p.id === id) || null;
    }
  },

  createProducto: async (data: any): Promise<Producto> => {
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'imagen' && value instanceof File) {
            formData.append(key, value);
          } else if (key === 'precio_compra' || key === 'precio_venta') {
            // Para precios, redondear a entero (sin decimales)
            const roundedValue = Math.round(Number(value));
            formData.append(key, roundedValue.toString());
          } else {
            formData.append(key, value.toString());
          }
        }
      });
      
      const response = await api.post<Producto>('/productos/productos/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error al crear producto:', error);
      throw new Error('No se pudo crear el producto. El backend no está disponible.');
    }
  },

  updateProducto: async (id: number, data: any): Promise<Producto> => {
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (value instanceof File) {
            // Si es un archivo (imagen), agregarlo directamente
            formData.append(key, value);
            console.log(`📎 FormData: ${key} = [File: ${value.name}]`);
          } else {
            // Para precios, redondear a entero (sin decimales)
            if (key === 'precio_compra' || key === 'precio_venta') {
              const roundedValue = Math.round(Number(value));
              formData.append(key, roundedValue.toString());
              console.log(`📎 FormData: ${key} = ${roundedValue} (redondeado de ${value})`);
            } else {
              // Para otros valores, convertir a string
              formData.append(key, value.toString());
              console.log(`📎 FormData: ${key} = ${value}`);
            }
          }
        }
      });
      
      // Usar PATCH para actualizaciones parciales
      const response = await api.patch<Producto>(`/productos/productos/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ Error al actualizar producto:', error);
      console.error('❌ Detalles del error:', error.response?.data);
      console.error('❌ Status:', error.response?.status);
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'No se pudo actualizar el producto.');
    }
  },

  deleteProducto: async (id: number): Promise<void> => {
    try {
      await api.delete(`/productos/productos/${id}/`);
    } catch (error) {
      console.error('❌ Error al eliminar producto:', error);
      throw new Error('No se pudo eliminar el producto. El backend no está disponible.');
    }
  },
};